import React from 'react';
import { IntegrationWizard } from 'features/integration-wizard';
import { ShellAppContextProvider } from 'features/shell/context/ShellAppContextProvider';
import { TelemetryRouteTracker } from 'features/telemetry/TelemetryRouteTracker';
import FloatingLanguageSwitcher from 'components/FloatingLanguageSwitcher';
import { appInIframe, getAdminProductId, wsClient } from 'util/appUtils';
import { useLocation } from 'react-router-dom';
import WizardStepper from '../components/kea/welcome/wizzard/WizardStepper';
import CloudDataWizard from './CloudDataWizard';
import { ShellSessionGate } from './ShellSessionGate';

/**
 * WizardShell — fullscreen surface for /app/:routeShopId/setup.
 *
 * Render contract (04-layout-navigation-and-containers-v1.md — Wizard Surface):
 * - No top navigation.
 * - No normal AppShell container chrome.
 * - Provides ShellAppContextProvider so EmbeddedWizard steps have access to
 *   shell-supplied bridge/gateway clients (Step 5 context).
 * - Completion routes to /app/:routeShopId/home via IntegrationWizard's own
 *   history.replace logic (no onComplete prop required).
 * - FloatingLanguageSwitcher is position:fixed — no layout gutter needed.
 *   Both it and IntegrationWizard are direct children of wizard-shell so
 *   the language switcher overlays without nesting inside the content tree.
 */
const WizardShell = () => {
  const location = useLocation();
  const isSearchSetup = /\/search\/setup\/?$/.test(location.pathname);
  const isDataSetup = /\/app\/[^/]+\/setup\/?$/.test(location.pathname);
  const isEmbedded = appInIframe();
  const productId = getAdminProductId();
  const useCloudDataWizard = !isEmbedded && isDataSetup && !isSearchSetup;

  return (
    <ShellAppContextProvider
      cloudClient={wsClient}
      productId={productId}
    >
      <ShellSessionGate>
        <TelemetryRouteTracker />
        <div
          data-testid="wizard-shell"
          data-wizard-flow={useCloudDataWizard ? 'catalog-onboarding' : 'search-experience'}
          style={{ width: '100%', position: 'relative' }}
        >
          {!useCloudDataWizard && <FloatingLanguageSwitcher />}
          {useCloudDataWizard ? (
            <CloudDataWizard>
              <WizardStepper
                includeStorefrontIntegration={false}
                useRuntimeCatalogStatus
                surfaceVariant="cloud-data"
              />
            </CloudDataWizard>
          ) : <IntegrationWizard />}
        </div>
      </ShellSessionGate>
    </ShellAppContextProvider>
  );
};

export default WizardShell;
